import { ReactNode, useEffect, useMemo, useState } from 'react';
import { CommandPalette } from './CommandPalette';
import { Sidebar } from './Sidebar';
import { AreaTabs, useAreaStrip } from './AreaTabs';
import { ShellStatus } from './ShellStatus';
import { usePageWidthStamp } from '@/lib/pageWidth';
import { ShellSlotsContext } from '@/lib/shellSlots';
import { useAuth } from '@/auth/useAuth';
import { isOperatorExperience } from '@/auth/permissions';

interface DesktopLayoutProps {
  children: ReactNode;
}

/**
 * The desktop shell: the sidebar (Sidebar.tsx — the seven destinations,
 * collapsible to an icon strip), ONE top row, the page, and the ⌘K palette.
 *
 * One row since 2026-10-10. Michael chose "Both rows + slimmer page headers"
 * — "it uses too much space"; most staff work on an ordinary laptop. The
 * shared NADC bar had gone the day before (its launcher, My Tickets, Get Help
 * and Sign out are the NADC apps button beside the bell). The second row was
 * this shell's own header: the sync light, a centred "Project Management
 * Portal" title (2026-09-17) and the bell. It went too — 56px for a name the
 * sidebar's wordmark already gives — and the area's tab strip moved up into
 * its place. So the row is:
 *
 * - on the left, the area's tabs and its actions (Job Blast); or, on a route
 *   with no tabs (Today, Tools and Admin, an area cut to one tab), a DOCK the
 *   page's own title row moves up into (lib/shellSlots.tsx), so the row still
 *   says where you are;
 * - on the right, the connection light, the bell and NADC apps (ShellStatus).
 *
 * The row is the same height on every route, so content never jumps between
 * areas and the bell never moves. It never scrolls and never wraps: only the
 * tab list scrolls sideways when the tabs outgrow it.
 */
export function DesktopLayout({ children }: DesktopLayoutProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { user } = useAuth();
  // An operator's nav is My job alone (spec §2.3): the sync badge still
  // shows the queue, the Sync Center link does not.
  const operator = isOperatorExperience(user);
  // Stamps data-page-width on <html> for the page-width preference; the
  // mobile layout is always fluid and needs nothing.
  usePageWidthStamp();

  // The area's tabs where it has some; the dock where it does not.
  const hasStrip = useAreaStrip() !== null;
  // Both slots are elements in the row, handed to the page through context.
  // Set from ref callbacks, so a docked title moves in the same commit as the
  // row appears — before the browser paints, with no flash of it in the page.
  const [dock, setDock] = useState<HTMLDivElement | null>(null);
  const [subbar, setSubbar] = useState<HTMLDivElement | null>(null);
  const slots = useMemo(() => ({ dock: hasStrip ? null : dock, subbar }), [hasStrip, dock, subbar]);

  // ⌘K on a Mac, Ctrl+K everywhere else. There is no button for it any more
  // (Michael, 2026-09-08) — the palette is keyboard-only.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* The banner landmark, outside <main> so it never scrolls. No
            overflow on it or the cluster: the bell's and NADC apps' popovers
            hang below it and would be clipped. 48px with a mouse, 56 on a
            touch screen (an iPad in landscape gets this shell). The first tab
            label and the dock both start 24px in, where the page's own
            content does. */}
        <header
          data-testid="shell-row"
          data-guide-chrome="top-bar"
          className="relative flex h-12 shrink-0 items-stretch gap-3 border-b border-graphite-800 bg-graphite-900 px-3 [@media(pointer:coarse)]:h-14"
        >
          {hasStrip ? (
            <AreaTabs variant="bar" />
          ) : (
            <div ref={setDock} data-testid="shell-dock" className="flex min-w-0 flex-1 items-center pl-3" />
          )}
          <ShellStatus operator={operator} />
          {/* Under the row, right of the sidebar: the project page's sticky
              bar. top-full is the row's padding edge, so mt-px leaves the
              row its bottom border. */}
          <div ref={setSubbar} data-testid="shell-subbar" className="absolute inset-x-0 top-full z-40 mt-px" />
        </header>

        {/* No padding of its own: PageContainer's p-6 is the page's gutter. */}
        <main className="flex-1 overflow-y-auto">
          <ShellSlotsContext.Provider value={slots}>{children}</ShellSlotsContext.Provider>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
